import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Ship, Palmtree, Sparkles } from "lucide-react";
import Reveal from "../components/common/Reveal";
import { HERO_VIDEO_URL } from "../components/common/Brand";
import { proxied } from "../lib/assets";

const DESTINATIONS = [
  { icon: Sparkles, title: "Luxury Escapes", blurb: "Overwater villas, boutique hotels, adults-only retreats.", img: "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85" },
  { icon: Ship, title: "Cruise Escapes", blurb: "Ocean & river. Caribbean, Mediterranean, Alaska, Europe.", img: "https://images.unsplash.com/photo-1667661931030-198fb917bd08?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85" },
  { icon: Compass, title: "Bucket List", blurb: "Safaris, Northern Lights, Italy, Greece & beyond.", img: "https://images.unsplash.com/photo-1764977224678-65ab06ea020a?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85" },
  { icon: Palmtree, title: "Relax & Recharge", blurb: "Spa retreats, quiet islands, beachfront resorts.", img: "https://images.unsplash.com/photo-1506929562872-bb421503ef21?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85" },
];

const MARQUEE = ["Adults-Only", "Overwater Villas", "Bucket List", "Safaris", "River Cruises", "European Escapes", "Northern Lights", "Scuba", "Celebration Travel"];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] bg-nightsky overflow-hidden flex items-center">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1540541338287-41700207dee6?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
        >
          <source src={proxied(HERO_VIDEO_URL)} type="video/quicktime" />
          <source src={proxied(HERO_VIDEO_URL)} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/80" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-3xl">
            <div className="mb-10 animate-fade-up" data-testid="hero-eyebrow">
              <div className="inline-flex items-center gap-4 mb-5">
                <span className="h-px w-12 bg-gold" />
                <span className="text-gold text-[11px] uppercase tracking-[0.4em]">Bespoke Travel</span>
              </div>
              <div className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ivory leading-none tracking-tight">
                Luxe<span className="text-gold">T</span>ini <span className="italic text-gold">Escapes</span>
              </div>
            </div>
            <h1 className="font-serif text-ivory text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight animate-fade-up" data-testid="hero-title" style={{ animationDelay: "120ms" }}>
              Escape the Ordinary.<br />
              <span className="italic text-gold">Travel Exceptionally.</span>
            </h1>
            <p className="mt-8 text-ivory/80 text-lg sm:text-xl max-w-xl font-light leading-relaxed animate-fade-up" style={{ animationDelay: "180ms" }} data-testid="hero-sub">
              Every journey, thoughtfully designed to reflect your style, pace, and priorities. You dream it. I handle the details.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 animate-fade-up" style={{ animationDelay: "340ms" }}>
              <Link
                to="/plan-your-escape"
                className="inline-flex items-center gap-3 h-14 px-8 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
                data-testid="hero-cta-primary"
              >
                Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-3 h-14 px-8 border border-ivory/40 text-ivory hover:bg-ivory hover:text-ink transition-colors text-[12px] uppercase tracking-[0.28em]"
                data-testid="hero-cta-secondary"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        <Link to="/services#sample-escapes" className="absolute bottom-8 left-0 right-0 z-10 overflow-hidden block group" data-testid="hero-marquee-link">
          <div className="marquee-track flex gap-14 whitespace-nowrap text-gold uppercase tracking-[0.4em] text-[11px] group-hover:text-ivory transition-colors">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i}>✦ {m}</span>
            ))}
          </div>
        </Link>
      </section>

      {/* INTRO / WELCOME */}
      <section className="py-24 lg:py-36 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="sticky top-28">
              <div className="star-sep mb-8">The Invitation</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-light">
                Every journey, <span className="italic text-teal">thoughtfully</span> designed.
              </h2>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <p className="text-lg lg:text-xl text-muted leading-[1.8] font-light">
              At <span className="text-ink font-normal">LuxeTini Escapes</span>, no two escapes are the same. Whether you're dreaming of a luxury, adults-only escape — a once-in-a-lifetime bucket list adventure — or a perfectly curated getaway to simply breathe again, every detail is handled so your only job is to arrive and enjoy.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                ["🍸", "Adults-Only Luxury", "/services"],
                ["🌍", "Bucket List Adventures", "/services"],
                ["🏝️", "Rest & Recharge", "/services"],
              ].map(([icon, label, href], i) => (
                <Link key={i} to={href} className="border-t border-hairline pt-5 group block hover:border-gold transition-colors" data-testid={`intro-tile-${i}`}>
                  <div className="text-2xl mb-2">{icon}</div>
                  <div className="text-sm uppercase tracking-[0.2em] text-ink group-hover:text-gold transition-colors flex items-center gap-1">
                    {label}
                    <ArrowUpRight size={12} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/plan-your-escape"
              className="mt-12 inline-flex items-center gap-2 text-ink border-b border-ink pb-1 text-[12px] uppercase tracking-[0.28em] hover:text-gold hover:border-gold transition-colors"
              data-testid="intro-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DESTINATIONS BY ESCAPE STYLE */}
      <section className="py-20 lg:py-28 bg-nightsky text-ivory relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-50" />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
              <div>
                <div className="star-sep mb-6">Escape Styles</div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory font-light leading-[1.05]">
                  Destinations by<br /><span className="italic text-gold">escape style.</span>
                </h2>
              </div>
              <p className="max-w-md text-ivory/70 font-light">
                Choose the feeling first. Let the destination follow.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="destinations-grid">
            {DESTINATIONS.map((d, i) => (
              <Reveal key={d.title} delay={i * 80}>
                <Link to="/services" className="group block relative tile aspect-[4/3] overflow-hidden" data-testid={`destination-card-${d.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <img src={d.img} alt={d.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    <d.icon size={22} strokeWidth={1.25} className="text-gold mb-3" />
                    <div className="font-serif text-3xl lg:text-4xl text-ivory">{d.title}</div>
                    <p className="mt-2 text-ivory/80 text-sm max-w-sm">{d.blurb}</p>
                    <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-gold inline-flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore <ArrowUpRight size={14} strokeWidth={1.5} />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE / QUOTE */}
      <section className="py-28 lg:py-40 px-6 lg:px-10 bg-ivory">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="star-sep justify-center mb-10"><span>A Promise</span></div>
            <p className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.12] font-light text-ink">
              “Life is too short for <span className="italic text-teal">ordinary</span> vacations. You deserve experiences that are seamless, meaningful, and completely tailored to <span className="italic text-gold">you</span>.”
            </p>
            <div className="mt-10 text-sm uppercase tracking-[0.3em] text-muted">— Tricia, Chief Escape Artist</div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE TEASER */}
      <section className="py-20 lg:py-28 px-6 lg:px-10 bg-[#F3EDE1]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <div className="star-sep mb-8">Three Levels of Service</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]">
              From the <span className="italic">Easy Escape</span> to the <span className="italic text-gold">Signature Escape</span>.
            </h2>
            <p className="mt-8 text-lg text-muted leading-relaxed font-light max-w-lg">
              Whether you want a simple starting point or a fully curated, white-glove experience — there's a level of service designed to meet you exactly where you are.
            </p>
            <Link
              to="/services"
              className="mt-10 inline-flex items-center h-14 px-8 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="services-cta"
            >
              Explore Services
            </Link>
          </Reveal>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { tier: "Easy Escape", price: "Complimentary", desc: "Simple. Straightforward. Supported.", param: "easy" },
              { tier: "Elevated Escape", price: "from $149", desc: "Personalized. Thoughtful. Seamless.", param: "elevated" },
              { tier: "Signature Escape", price: "from $349", desc: "Curated. Elevated. Effortless.", param: "signature" },
            ].map((t, i) => (
              <Reveal key={t.tier} delay={i * 100} className={i === 2 ? "sm:col-span-2" : ""}>
                <Link to={`/plan-your-escape?tier=${t.param}`} className="block border border-ink/10 bg-ivory p-8 h-full hover:border-gold transition-colors group" data-testid={`home-tier-card-${t.param}`}>
                  <div className="text-xs uppercase tracking-[0.26em] text-gold mb-4">{t.price}</div>
                  <div className="font-serif text-2xl lg:text-3xl text-ink group-hover:text-gold transition-colors">{t.tier}</div>
                  <p className="mt-3 text-sm text-muted font-light">{t.desc}</p>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-ink/60 group-hover:text-gold inline-flex items-center gap-1">
                    Start with this level <ArrowUpRight size={12} strokeWidth={1.5} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-10">
              <div>
                <div className="star-sep mb-6">Captured Escapes</div>
                <h2 className="font-serif text-4xl sm:text-5xl font-light">Real moments. Real memories.</h2>
              </div>
              <Link to="/gallery" className="text-[12px] uppercase tracking-[0.28em] border-b border-ink pb-1 hover:text-gold hover:border-gold" data-testid="gallery-preview-cta">
                View the Gallery
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
              "https://images.unsplash.com/photo-1540541338287-41700207dee6?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
              "https://images.unsplash.com/photo-1469474968028-56623f02e42e?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
              "https://images.unsplash.com/photo-1559599746-8823b38544c6?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
            ].map((src, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className={`tile aspect-[3/4] ${i % 2 === 1 ? "mt-10" : ""}`}>
                  <img src={src} alt="Escape" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-10 bg-ink text-ivory overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          src={proxied("https://customer-assets.emergentagent.com/job_escape-design-lab/artifacts/1t8ahve3_4983132_Aerial_View_Alone_3840x2160.mp4")}
          poster="https://images.unsplash.com/photo-1501117716987-c8e1ecb210bc?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="cta-banner-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/50 to-ink/80" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="star-sep justify-center mb-8"><span className="text-white">Your Next Escape</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] text-white">
              Dream big.<br /><span className="italic text-gold">I'll handle the details.</span>
            </h2>
            <Link
              to="/plan-your-escape"
              className="mt-12 inline-flex items-center gap-3 h-14 px-10 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="bottom-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
